import * as Api from '@ViewModels';

/** Loosely-typed V2 question shape as held in RHF `surveyForm.surveyParts` form state. */
interface V2Question {
	id?: string;
	fieldId?: string;
	label?: string;
	fieldType?: Api.FormFieldType;
	isOptional?: boolean;
	isHidden?: boolean;
	options?: Array<{ id?: string; label?: string; fieldId?: string }>;
}

type V2Part = V2Question & { _type?: string; questions?: V2Question[] };

/**
 * Flatten a V2 survey's `surveyForm.surveyParts` (sections + questions) into the flat
 * `customForm.fields` shape.
 *
 * This is the single definition of "V2 questions → renderable/saveable fields". It is used
 * by BOTH:
 * - the save path (`createCustomForm` builds the PUT payload's `customForm.fields` with it —
 *   the BE requires `fields[*].id` to match question fieldIds for visibility-ref resolution), and
 * - the Preview tab (which renders question labels/options from a flat fields array and must
 *   mirror the editor's LIVE form state — unsaved labels, options, and newly added questions
 *   included, exactly like the V1 editor and the other survey types).
 *
 * Returns `null` when `surveyParts` contains no `SurveySection` (a V1 survey) — callers fall
 * back to the V1 behavior (`customForm.fields` passthrough), mirroring `buildSectionGroups`'s
 * contract.
 */
export function flattenSurveyPartsToFields(surveyParts: unknown[] | undefined): Api.IFormField<string>[] | null {
	const parts = (surveyParts ?? []) as V2Part[];
	// `_type === 'SurveySection'` mirrors the `isSurveySection` guard in
	// QuestionsWithSections/types.ts — duplicated here so this util stays a pure data
	// helper with no component-tree type imports.
	if (!parts.some(p => p?._type === 'SurveySection')) return null;

	const flatFields: Api.IFormField<string>[] = [];
	for (const part of parts) {
		if (part._type === 'SurveySection') {
			for (const q of part.questions ?? []) {
				flatFields.push({
					id: q.fieldId ?? q.id ?? '',
					label: q.label ?? '',
					fieldType: q.fieldType ?? Api.FormFieldType.String,
					isOptional: q.isOptional ?? false,
					options: q.options ?? [],
					// Carry the admin-hidden flag through (hydrated onto V2 questions by
					// denormalizeSurveyFormFromFields) — hardcoding false un-hid fields on
					// save and rendered them in Preview.
					isHidden: q.isHidden ?? false,
				});
			}
		} else {
			// Bare question at root (legacy V1 shape under V2 wrapper)
			flatFields.push({
				id: part.fieldId ?? part.id ?? '',
				label: part.label ?? '',
				fieldType: part.fieldType ?? Api.FormFieldType.String,
				isOptional: part.isOptional ?? false,
				options: part.options ?? [],
				isHidden: part.isHidden ?? false,
			});
		}
	}
	return flatFields;
}
